import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { 
  Download, ArrowLeft, Loader2, FileText, Layers, Calendar, Image, 
  ThumbsUp, ThumbsDown, MessageSquare, Sparkles, Palette, Settings2,
  DollarSign, Clock, Timer, User, Building, RefreshCw, Upload, AlertCircle, CheckCircle2,
  AlertTriangle
} from "lucide-react";
import { format } from "date-fns";

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
const fmtDate = (d) => { try { return format(new Date(d), "MMM d, yyyy h:mm a"); } catch { return d || "—"; } };
const fmtDuration = (s) => {
  if (!s) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
};
const fmtCost = (cost) => {
  if (!cost) return "—";
  if (cost.cost_usd !== undefined) return `$${Number(cost.cost_usd).toFixed(4)}`;
  const total = (cost.input_cost || 0) + (cost.output_cost || 0);
  return total > 0 ? `$${total.toFixed(4)}` : "—";
};
const fmtTokens = (cost) => {
  if (!cost) return { input: "—", output: "—" };
  return {
    input: cost.input_tokens?.toLocaleString() || "—",
    output: cost.output_tokens?.toLocaleString() || "—"
  };
};

export default function AdminDetailsPage() {
  const { id, type } = useParams();
  const navigate = useNavigate();
  const isDeck = type === "deck";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contentFiles, setContentFiles] = useState([]);
  const [styleFiles, setStyleFiles] = useState([]);
  const [retrying, setRetrying] = useState(false);
  const [showReupload, setShowReupload] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [reuploadNotes, setReuploadNotes] = useState("");
  const [uploading, setUploading] = useState(false);
  const [activeSlideIdx, setActiveSlideIdx] = useState(0);
  const [imgVersion, setImgVersion] = useState(0);
  // Combine local imgVersion (bumped on reupload) with the row's persisted
  // _version (bumped server-side on every reupload). Either changing busts
  // the browser's thumbnail cache — important on prod where Cloudflare's
  // edge caching can persist images across pod restarts.
  const cacheKey = (data?._version ?? 0) + imgVersion;

  const [slides, setSlides] = useState([]);
  // Admin debug payload — full prompts + raw Claude responses across the
  // storyline / split / per-slide pipelines. Lazily fetched on first
  // mount because it's heavy (full prompts + assistant responses).
  const [debug, setDebug] = useState(null);
  const [debugLoading, setDebugLoading] = useState(true);

  const fetchData = async () => {
    try {
      const endpoint = isDeck ? `${API}/decks/${id}` : `${API}/presentations/${id}`;
      const res = await axios.get(endpoint, { withCredentials: true });
      setData(res.data);
      
      // For decks, fetch individual slide data to get costs
      if (isDeck && res.data.slide_ids?.length > 0) {
        const slidePromises = res.data.slide_ids.map(sid => 
          axios.get(`${API}/presentations/${sid}`, { withCredentials: true })
            .then(r => r.data)
            .catch(() => ({ id: sid, cost: null }))
        );
        const slideData = await Promise.all(slidePromises);
        setSlides(slideData);
      }
      
      // Fetch file details for attachments
      const fetchFiles = async (ids) => {
        const files = [];
        for (const fid of ids || []) {
          try {
            const r = await axios.get(`${API}/files/${fid}`, { withCredentials: true });
            files.push(r.data);
          } catch { files.push({ id: fid, filename: fid, thumbnail_url: null }); }
        }
        return files;
      };
      
      // Content files (raw data attachments)
      const contentIds = res.data.file_ids || res.data.content_image_ids || res.data.inputs?.content_image_ids || [];
      setContentFiles(await fetchFiles(contentIds));
      
      // Style reference files
      const styleIds = res.data.inspiration_file_ids || res.data.inputs?.inspiration_file_ids || [];
      setStyleFiles(await fetchFiles(styleIds));
      
    } catch { toast.error("Failed to load details"); }
    finally { setLoading(false); }
  };

  // Fetch the admin debug payload (prompts + raw Claude responses).
  // Independent from the main `fetchData()` so a debug-endpoint failure
  // does not block the rest of the page from loading.
  const fetchDebug = async () => {
    setDebugLoading(true);
    try {
      const t = isDeck ? "deck" : "slide";
      const r = await axios.get(`${API}/admin/debug/${t}/${id}`, { withCredentials: true });
      setDebug(r.data);
    } catch {
      setDebug(null);
    } finally {
      setDebugLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchDebug();
    // Poll for updates if generating
    const interval = setInterval(() => {
      if (data?.status === "generating") {
        fetchData();
        fetchDebug();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [id, isDeck]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDownload = () => {
    // Let the server's Content-Disposition pick the slugified <title>.pptx.
    const a = document.createElement("a");
    a.href = isDeck ? `${API}/download-deck/${id}` : `${API}/download/${id}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      const endpoint = isDeck ? `${API}/decks/${id}/retry` : `${API}/presentations/${id}/retry`;
      await axios.post(endpoint, {}, { withCredentials: true });
      toast.success("Retrying generation...");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to retry");
    } finally {
      setRetrying(false);
    }
  };

  const handleRetryMergeOnly = async () => {
    setRetrying(true);
    try {
      await axios.post(`${API}/decks/${id}/retry-merge`, {}, { withCredentials: true });
      toast.success("Re-merging existing slides — no Opus regeneration");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to retry merge");
    } finally {
      setRetrying(false);
    }
  };

  const handleReupload = async () => {
    if (!selectedFile) { toast.error("Please select a file"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      if (reuploadNotes) formData.append("notes", reuploadNotes);
      await axios.post(`${API}/admin/logs/${type}/${id}/reupload`, formData, { withCredentials: true });
      toast.success("File reuploaded successfully");
      setShowReupload(false);
      setSelectedFile(null);
      setReuploadNotes("");
      // Bust the browser's thumbnail cache. Every <img> on this page
      // appends `?v=imgVersion` so bumping the counter forces a fresh
      // fetch — without this, the new cover/per-slide PNGs sit on disk
      // but the browser keeps serving the cached old version and the
      // admin (rightly) reports "nothing changed".
      setImgVersion(v => v + 1);
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Failed to reupload"); 
    } finally { 
      setUploading(false); 
    }
  };

  if (loading) return <div className="page-container flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  if (!data) return <div className="page-container"><p className="text-gray-500">Not found</p></div>;

  const status = data.status || "complete";
  const canDownload = status === "complete" && data.file_path;
  const slideIds = data.slide_ids || [];
  const tokens = fmtTokens(data.cost);

  // Cumulative cost across ALL attempts (current + every prior auto/manual
  // retry that landed in cost_history). For decks we additionally aggregate
  // each individual slide's cost + the storyline split cost on top.
  // Surfaces the TRUE spend in the admin KPI strip — without this, retries
  // silently overwrite `cost` and admins underestimate the bill.
  const calculateTotalCost = () => {
    let totalCostUsd = 0;
    let totalInputTokens = 0;
    let totalOutputTokens = 0;
    let totalCacheCreation = 0;
    let totalCacheRead = 0;

    const accumulate = (c) => {
      if (!c) return;
      totalCostUsd += c.cost_usd || 0;
      totalInputTokens += c.input_tokens || 0;
      totalOutputTokens += c.output_tokens || 0;
      totalCacheCreation += c.cache_creation_tokens || 0;
      totalCacheRead += c.cache_read_tokens || 0;
    };

    accumulate(data.cost);
    (data.cost_history || []).forEach(accumulate);
    // Stage 1 is the Opus transform that produces the slide brief —
    // tracked separately so admins can see its share of the total bill.
    accumulate(data.stage1_cost);

    if (isDeck) {
      accumulate(data.split_cost);
      // Each slide's own cost (and its own retry history).
      for (const s of slides) {
        accumulate(s.cost);
        (s.cost_history || []).forEach(accumulate);
        accumulate(s.stage1_cost);
      }
    }

    return {
      cost_usd: totalCostUsd,
      input_tokens: totalInputTokens,
      output_tokens: totalOutputTokens,
      cache_creation_tokens: totalCacheCreation,
      cache_read_tokens: totalCacheRead,
    };
  };

  // Number of attempts captured (current run + archived history).
  // Used for the "across N attempts" sub-label on the cumulative cost cell.
  const attemptCount = 1 + (data.cost_history?.length || 0);

  const totalCost = calculateTotalCost();
  const totalTokens = fmtTokens(totalCost);

  // Approach + research-tier indicators (mirrors UserDetailsPage so admins
  // see exactly what the user sees, plus the tier label for billing context).
  const isResearch = !!data.research_provider || !!data.inputs?.research_text;
  const provider = (data.research_provider || "").toLowerCase();
  const isPremium = provider.startsWith("premium");
  const tierLabel = isPremium ? "Premium data" : "Public data";

  // Extract user-entered text fields (NEVER `data.content` /
  // `inputs.content` — those carry Opus-generated slide output, not user
  // input, and surfacing them confuses admins reviewing what was submitted).
  const rawData = data.inputs?.raw_text || data.raw_text || "";
  const topic = data.topic || data.inputs?.topic || "";

  // Page title — for decks use topic, for singles use user-entered title.
  // Fall back to a generic placeholder for legacy slide rows that
  // pre-date the inputs.title field (rather than leaking generated content).
  const slideTitle = data.inputs?.title || data.title || "";
  const pageTitle = isDeck
    ? (topic || "Untitled Deck")
    : (slideTitle || "Untitled Slide");

  // Decks: user-typed content lives under `inputs.content_text`.
  const deckRawContent = isDeck ? (data.inputs?.content_text || "") : "";
  // Raw HasData research prose grounded into the AI along with the system
  // prompt — surfaced under the "Research output" card.
  const researchText = data.inputs?.research_text || "";

  return (
    <div className="page-container" data-testid="admin-details-page">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-400 mb-4">
        <button onClick={() => navigate("/admin/logs")} className="hover:text-gray-600 flex items-center gap-1" data-testid="back-btn">
          <ArrowLeft className="w-3.5 h-3.5" />Admin Logs
        </button>
        <span>/</span><span className="text-gray-900">Details</span>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl sm:text-2xl font-bold font-['Manrope'] max-w-md truncate" data-testid="page-title" title={pageTitle}>
            {pageTitle}
          </h1>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${isDeck ? "text-indigo-700 bg-indigo-50 border border-indigo-200" : "text-gray-600 bg-gray-100 border border-gray-200"}`}>
            {isDeck ? <><Layers className="w-3 h-3" />Deck</> : <><FileText className="w-3 h-3" />Slide</>}
          </span>
          <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 ${status === "complete" ? "text-green-700 bg-green-50 border border-green-200" : status === "failed" ? "text-red-600 bg-red-50 border border-red-200" : "text-amber-600 bg-amber-50 border border-amber-200"}`}>
            {status === "complete" ? <CheckCircle2 className="w-3 h-3" /> : status === "failed" ? <AlertCircle className="w-3 h-3" /> : <Loader2 className="w-3 h-3 animate-spin" />}
            {capitalize(status)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {status === "failed" && (
            <>
              {/* Merge-only retry — visible only on decks that failed at
                  merge (i.e. slides are still complete in GridFS).
                  Shipping in production: ~30s vs. 10-30min for a full
                  Opus regeneration, and skips re-paying Opus tokens for
                  slides we already generated. Backend guards against
                  abuse by re-checking every slide is complete with
                  reachable bytes before re-invoking the merge service. */}
              {isDeck && data.merge_failure_reason && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleRetryMergeOnly}
                  disabled={retrying}
                  data-testid="retry-merge-only-btn"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  title={`Re-run merge only. Last failure: ${data.merge_failure_reason}`}
                >
                  {retrying
                    ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                    : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Retry merge only
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={handleRetry} disabled={retrying} data-testid="retry-btn">
                {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                Retry
              </Button>
            </>
          )}
          {canDownload && (
            <>
              <Button size="sm" variant="outline" onClick={() => setShowReupload(true)}>
                <Upload className="w-3.5 h-3.5 mr-1" />Reupload
              </Button>
              <Button size="sm" onClick={handleDownload} className="bg-black hover:bg-gray-800" data-testid="download-btn">
                <Download className="w-3.5 h-3.5 mr-1" />Download
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Admin-only: Cost & Performance Metrics */}
      <Card className="border-blue-200 bg-blue-50/50 shadow-none mb-6" data-testid="admin-kpi-strip">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold text-blue-800 flex items-center gap-2">
            <DollarSign className="w-4 h-4" />Admin Metrics (Internal Only)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
            <div data-testid="kpi-credits">
              <p className="text-xs text-blue-600 mb-1">Credits Utilized</p>
              <p className="text-lg font-bold text-blue-900">
                {data.credits_charged != null ? data.credits_charged : <span className="text-blue-400">—</span>}
                {data.credits_charged == null && data.user?.role === "admin" && (
                  <span className="text-[10px] text-blue-400 ml-1">(admin exempt)</span>
                )}
              </p>
            </div>
            <div data-testid="kpi-time-taken">
              <p className="text-xs text-blue-600 mb-1">Time Taken</p>
              <p className="text-lg font-bold text-blue-900">{fmtDuration(data.time_taken)}</p>
            </div>
            <div data-testid="kpi-approach">
              <p className="text-xs text-blue-600 mb-1">Approach</p>
              <p className="text-sm font-semibold text-blue-900 leading-tight">
                {isResearch ? "Wokelo research agent" : "Only my content"}
              </p>
              {isResearch && (
                <span
                  className={`inline-block mt-1 text-[10px] font-semibold rounded-full px-2 py-0.5 border ${isPremium ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-teal-100 text-teal-800 border-teal-200"}`}
                  data-testid="kpi-data-tier"
                >
                  {tierLabel}
                </span>
              )}
            </div>
            <div data-testid="kpi-total-cost">
              <p className="text-xs text-blue-600 mb-1">
                Cumulative Cost
                {attemptCount > 1 && (
                  <span className="ml-1 text-[10px] font-semibold text-blue-700">
                    (across {attemptCount} attempts)
                  </span>
                )}
              </p>
              <p className="text-lg font-bold text-blue-900">{fmtCost(totalCost)}</p>
              {attemptCount > 1 && data.cost?.cost_usd != null && (
                <p className="text-[10px] text-blue-500 mt-0.5">
                  Latest run: ${Number(data.cost.cost_usd).toFixed(4)}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-blue-600 mb-1">Input Tokens</p>
              <p className="text-lg font-bold text-blue-900">{totalTokens.input}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600 mb-1">Output Tokens</p>
              <p className="text-lg font-bold text-blue-900">{totalTokens.output}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600 mb-1">Retry Count</p>
              <p className="text-lg font-bold text-blue-900">{data.retry_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-blue-600 mb-1">Engine</p>
              <p className="text-lg font-bold text-blue-900 capitalize">{data.engine || "Advanced"}</p>
            </div>
          </div>
          {data.split_cost && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-blue-600 mb-1">Storyline Split Cost</p>
              <p className="text-sm text-blue-800">
                {fmtCost(data.split_cost)} ({data.split_cost?.input_tokens?.toLocaleString() || 0} in / {data.split_cost?.output_tokens?.toLocaleString() || 0} out)
              </p>
            </div>
          )}
          {status === "failed" && data.status_detail && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-red-600 mb-1">Error Details</p>
              <p className="text-sm text-red-800 font-mono bg-red-50 rounded p-2">{data.status_detail}</p>
            </div>
          )}
          {status === "generating" && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <p className="text-xs text-amber-600 mb-1">Current Status</p>
              <p className="text-sm text-amber-800">{data.status_detail || "Processing..."}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Cost Breakdown ──────────────────────────────────────────────
         Surfaces the actual cost drivers for high-bill decks/slides:
         • Stage 1 vs Stage 2 split (where did the spend go?)
         • Cache-creation vs cache-read tokens (cache misses are 4x cost)
         • Resume-loop count (15 loops can 3-5x the bill)
         • Attachment counts (images, content files, design refs)
         For decks, aggregates per-slide rows so a single glance answers
         "is the deck expensive because of N slides each costing $X, or
         because one slide ran away?".
      */}
      <CostBreakdownCard
        data={data}
        slides={isDeck ? slides : null}
        isDeck={isDeck}
        totalCost={totalCost}
        fmtCost={fmtCost}
      />

      {/* User Info */}
      {data.user && (
        <Card className="border-gray-200 shadow-none mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <User className="w-4 h-4 text-gray-500" />User Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {data.user.picture && (
                <img src={data.user.picture} alt={data.user.name} className="w-10 h-10 rounded-full" />
              )}
              <div>
                <p className="font-medium text-gray-900">{data.user.name || "Unknown"}</p>
                <p className="text-sm text-gray-500">{data.user.email}</p>
              </div>
              {data.user.organization && (
                <div className="flex items-center gap-1 text-sm text-gray-500 ml-4">
                  <Building className="w-4 h-4" />{data.user.organization}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadata bar */}
      <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-6 pb-4 border-b border-gray-100">
        <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />{fmtDate(data.created_at)}</div>
        {data.completed_at && <div className="flex items-center gap-1"><Timer className="w-3 h-3" />Completed: {fmtDate(data.completed_at)}</div>}
        {isDeck && <div>{data.num_slides || slides.length} slides</div>}
        <div className="text-gray-600 font-mono text-xs" data-testid="item-id">ID: {id}</div>
      </div>

      <div className="space-y-6">
        {/* Big slide carousel — full-width, parity with the user details
            view so admins see exactly what the user sees, plus per-slide
            download CTAs for support workflows. */}
        <Card className="border-gray-200 shadow-none overflow-hidden" data-testid="admin-slide-main-view">
          <div className="aspect-video bg-gray-50 flex items-center justify-center relative">
            {status === "generating" ? (
              <div className="flex flex-col items-center text-amber-600">
                <Loader2 className="w-10 h-10 animate-spin mb-2" />
                <span className="text-sm font-medium">{data.status_detail || "Generating..."}</span>
              </div>
            ) : status === "failed" ? (
              <div className="flex flex-col items-center text-red-500">
                <span className="text-sm font-medium">Generation failed</span>
                {data.status_detail && <span className="text-xs mt-1 max-w-md text-center">{data.status_detail}</span>}
              </div>
            ) : (
              <img
                src={`${API}/thumbnails/${isDeck ? (slideIds[activeSlideIdx] || slideIds[0]) : data.id}?v=${cacheKey}`}
                alt={`Slide ${activeSlideIdx + 1}`}
                className="w-full h-full object-contain"
                data-testid="admin-active-slide-image"
                onError={(e) => { e.target.style.opacity = "0.2"; }}
                onLoad={(e) => { e.target.style.opacity = "1"; }}
              />
            )}

            {isDeck && slideIds.length > 1 && status === "complete" && (
              <>
                <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-full bg-black/70 text-white text-xs font-semibold">
                  {activeSlideIdx + 1} / {slideIds.length}
                </div>
                <button
                  onClick={() => {
                    const sid = slideIds[activeSlideIdx];
                    const a = document.createElement("a");
                    a.href = `${API}/download/${sid}`;
                    a.download = `slide_${activeSlideIdx + 1}.pptx`;
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  }}
                  className="absolute bottom-3 right-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-white/95 text-gray-800 text-xs font-semibold hover:bg-white transition-colors shadow"
                  data-testid={`admin-download-active-slide`}
                >
                  <Download className="w-3.5 h-3.5" />Download this slide
                </button>
              </>
            )}
          </div>
        </Card>

        {/* Horizontal thumbnail rail — decks only */}
        {isDeck && slideIds.length > 1 && (
          <div
            className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
            data-testid="admin-slide-thumbnail-carousel"
          >
            {slideIds.map((sid, i) => (
              <button
                key={sid}
                onClick={() => setActiveSlideIdx(i)}
                className={`flex-shrink-0 w-44 rounded-lg border-2 overflow-hidden transition-all snap-start ${
                  activeSlideIdx === i
                    ? "border-teal-500 ring-2 ring-teal-200"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`admin-thumb-${i}`}
              >
                <div className="aspect-video bg-gray-100 relative">
                  <img
                    src={`${API}/thumbnails/${sid}?v=${cacheKey}`}
                    alt={`Slide ${i + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.opacity = "0.3"; }}
                  />
                  <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded bg-black/70 text-white text-[10px] font-bold">
                    {i + 1}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Per-slide individual download grid — kept so admins can grab any
            single slide's PPTX during support flows. The carousel above
            handles primary navigation; this grid is the "show me all
            slides at a glance" affordance. */}
        {isDeck && slideIds.length > 1 && (
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Layers className="w-4 h-4 text-gray-500" />Individual Slides ({slideIds.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {slideIds.map((sid, i) => (
                  <div key={sid || i} className="group relative">
                    <div className="aspect-video rounded overflow-hidden border border-gray-200 bg-gray-50">
                      <img src={`${API}/thumbnails/${sid}?v=${cacheKey}`} alt={`Slide ${i + 1}`} className="w-full h-full object-cover" />
                    </div>
                    <div className="mt-1.5 flex items-center justify-between">
                      <span className="text-xs text-gray-500">Slide {i + 1}</span>
                      {status === "complete" && (
                        <button
                          onClick={() => {
                            const a = document.createElement("a");
                            a.href = `${API}/download/${sid}`;
                            a.download = `slide_${i + 1}.pptx`;
                            document.body.appendChild(a); a.click(); document.body.removeChild(a);
                          }}
                          className="text-xs text-gray-400 hover:text-black flex items-center gap-1"
                          data-testid={`download-slide-${i + 1}`}
                        >
                          <Download className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback */}
        {data.feedback && (
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-gray-500" />User Feedback
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 mb-2">
                {(data.feedback.rating === "up" || data.feedback.rating === "like") && <span className="flex items-center gap-1 text-green-600 text-sm"><ThumbsUp className="w-4 h-4" />Liked</span>}
                {(data.feedback.rating === "down" || data.feedback.rating === "dislike") && <span className="flex items-center gap-1 text-red-600 text-sm"><ThumbsDown className="w-4 h-4" />Disliked</span>}
                {!data.feedback.rating && <span className="text-sm text-gray-400">No rating</span>}
              </div>
              {data.feedback.feedback_text && (
                <p className="text-sm text-gray-600 italic bg-gray-50 rounded p-2">"{data.feedback.feedback_text}"</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Public-data accuracy warning — only shows for research+design
            runs grounded in PUBLIC sources. Mirrors the warning users see. */}
        {isResearch && !isPremium && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2"
            data-testid="admin-public-data-warning"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-900 leading-relaxed">
              External research using <strong>public data</strong> (~5% error rate). Verify all data before sharing.
            </p>
          </div>
        )}

        {/* Inputs grid — title/topic, content (user-entered ONLY), files,
            research output, template. Generated content (Opus output /
            storyline) is NOT shown here per spec — the slide preview above
            already captures the rendered output, and showing the raw
            generated text confused users into thinking it was their input. */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {isDeck && topic && (
            <Card className="border-gray-200 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Topic</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">{topic}</p>
              </CardContent>
            </Card>
          )}
          {!isDeck && slideTitle && (
            <Card className="border-gray-200 shadow-none">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold">Title</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-700">{slideTitle}</p>
              </CardContent>
            </Card>
          )}

          {/* Content input — user-entered text ONLY (raw_text for slides,
              content_text for decks). Never the generated slide content. */}
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />Content Input
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(rawData || deckRawContent) ? (
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans bg-gray-50 rounded-md p-3 max-h-48 overflow-y-auto">
                  {rawData || deckRawContent}
                </pre>
              ) : (
                <p className="text-sm text-gray-400 italic">No content input provided for this generation</p>
              )}
            </CardContent>
          </Card>

          {/* Content Attachments */}
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Image className="w-4 h-4 text-gray-500" />Content Attachments {contentFiles.length > 0 && `(${contentFiles.length})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {contentFiles.length > 0 ? (
                <div className="flex flex-wrap gap-3" data-testid="admin-content-files">
                  {contentFiles.map((f, i) => (
                    <FileChipDownload key={f.id || i} f={f} icon={FileText} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No attachments provided</p>
              )}
            </CardContent>
          </Card>

          {/* Style References */}
          <Card className="border-gray-200 shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Palette className="w-4 h-4 text-gray-500" />Style References {styleFiles.length > 0 && `(${styleFiles.length})`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {styleFiles.length > 0 ? (
                <div className="flex flex-wrap gap-3" data-testid="admin-style-files">
                  {styleFiles.map((f, i) => (
                    <FileChipDownload key={f.id || i} f={f} icon={Palette} />
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic">No style references provided (using template defaults)</p>
              )}
            </CardContent>
          </Card>

          {/* Admin-only: Full AI debug — every prompt + every Claude
              response across the storyline / split / per-slide pipelines.
              The data here comes from a separate `/api/admin/debug/{type}/{id}`
              call; the end-user `/details/:id` page never fetches this. */}
          <AdminAIDebugCard isDeck={isDeck} debug={debug} loading={debugLoading} />

          {/* Research output — RAW HasData prose fed into the AI. Renamed
              per spec: no AI/Opus mention; tier label distinguishes Public
              vs Premium. Skipped entirely for design-only runs. */}
          {researchText && (
            <Card className="border-violet-200 shadow-none bg-violet-50/30 lg:col-span-2" data-testid="admin-research-output">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-violet-900">
                  <Sparkles className="w-4 h-4 text-violet-600" />
                  Research output
                  <span
                    className={`ml-1 inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 border ${isPremium ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-teal-100 text-teal-800 border-teal-200"}`}
                  >
                    {tierLabel}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans bg-white border border-violet-100 rounded-md p-3 max-h-72 overflow-y-auto">
                  {researchText}
                </pre>
              </CardContent>
            </Card>
          )}

          {/* Template & Engine */}
          <Card className="border-gray-200 shadow-none lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-gray-500" />Generation Settings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Template</p>
                  <p className="text-gray-700 font-medium">{data.template_name || "Default"}</p>
                  {data.template_id && <p className="text-[10px] text-gray-400">{data.template_id}</p>}
                </div>
                <div>
                  <p className="text-xs text-gray-400 mb-1">PPT Engine</p>
                  <p className="text-gray-700 font-medium capitalize">{data.engine || "Advanced"}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin Notes */}
          {data.admin_notes && (
            <Card className="border-amber-200 bg-amber-50/50 shadow-none lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold text-amber-800">Admin Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-amber-900">{data.admin_notes}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Reupload Dialog */}
      <Dialog open={showReupload} onOpenChange={setShowReupload}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reupload Fixed {isDeck ? "Deck" : "Slide"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Upload Fixed PPTX</label>
              <input type="file" accept=".pptx" onChange={e => setSelectedFile(e.target.files?.[0] || null)} className="w-full" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">Admin Notes (Optional)</label>
              <Textarea placeholder="Notes about what was fixed..." value={reuploadNotes} onChange={e => setReuploadNotes(e.target.value)} className="min-h-[80px]" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReupload(false)}>Cancel</Button>
            <Button onClick={handleReupload} disabled={uploading || !selectedFile} className="bg-black hover:bg-gray-800">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// FileChipDownload — small thumbnail card with a hover-revealed download
// affordance. Used for both content attachments and style references on
// the admin details page so admins can pull originals during user
// support / impersonation flows. Hits the GET /files/{id}/download
// endpoint (admin-bypass on the backend).
// ─────────────────────────────────────────────────────────────────────
function FileChipDownload({ f, icon: Icon }) {
  const handleDownload = () => {
    if (!f?.id) return;
    const a = document.createElement("a");
    a.href = `${API}/files/${f.id}/download`;
    a.download = f.filename || f.id;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const FIcon = Icon || FileText;
  return (
    <button
      onClick={handleDownload}
      className="group flex flex-col items-center gap-1 focus:outline-none"
      title="Download"
      data-testid={`admin-file-download-${f.id}`}
    >
      <div className="w-20 h-14 rounded border border-gray-200 overflow-hidden bg-gray-50 relative group-hover:border-gray-900 transition-colors">
        {f.thumbnail_url ? (
          <img src={f.thumbnail_url} alt={f.filename} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FIcon className="w-5 h-5 text-gray-300" />
          </div>
        )}
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gray-900/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Download className="w-4 h-4 text-white" />
        </div>
      </div>
      <span className="text-[10px] text-gray-500 truncate max-w-20" title={f.filename}>
        {f.filename || f.id?.slice(0, 8)}
      </span>
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────────
// AdminAIDebugCard — full prompt + response trail for an admin to audit
// what the AI saw and what it produced. Replaces the earlier simpler
// "Generated slide content" card (which only surfaced the user-input
// text for singles and the split chunks for decks — confusing for
// admins, since those weren't AI OUTPUT but rather AI INPUT).
//
// Visible only on /admin/details/:type/:id. Backed by GET
// /api/admin/debug/{type}/{id}. End-user details page never fetches
// this endpoint and never renders this card.
//
// Renders three flavors of information depending on item type:
//
//   SINGLE SLIDE
//     • System prompt
//     • User prompt
//     • Assistant response text (all `text` blocks Claude emitted
//       alongside the binary PPTX skill output)
//     • User input content (for context — this is the row's `content`
//       field, NOT AI output)
//
//   DECK
//     [Storyline]
//       • System prompt (multi_prompt + structure)
//       • User prompt
//       • Raw storyline response (markdown)
//     [Split]
//       • Split prompt
//       • Split response (raw JSON array)
//     [Per slide × N]
//       • Slide N · Title
//       • Content chunk (what was fed into this slide's PPTX prompt)
//       • System prompt + User prompt + Assistant response
//
// Each section is collapsible (default collapsed for prompts, expanded
// for response) so the page doesn't become an unreadable wall of text.
// ─────────────────────────────────────────────────────────────────────
function AdminAIDebugCard({ isDeck, debug, loading }) {
  if (loading) {
    return (
      <Card className="border-gray-200 shadow-none lg:col-span-2" data-testid="admin-ai-debug-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-500" />
            AI debug
            <AdminOnlyBadge />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400 italic">Loading prompt + response trail…</p>
        </CardContent>
      </Card>
    );
  }

  if (!debug) {
    return (
      <Card className="border-gray-200 shadow-none lg:col-span-2" data-testid="admin-ai-debug-empty">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-500" />
            AI debug
            <AdminOnlyBadge />
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-400 italic">
            No debug data available — legacy rows pre-date prompt capture,
            or generation never completed.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-blue-200 bg-blue-50/30 shadow-none lg:col-span-2"
      data-testid="admin-ai-debug-card"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-900">
          <FileText className="w-4 h-4 text-blue-600" />
          AI debug — prompts + Claude responses
          <AdminOnlyBadge variant="solid" />
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {isDeck ? <DeckDebugBody debug={debug} /> : <SlideDebugBody debug={debug} />}
      </CardContent>
    </Card>
  );
}

function AdminOnlyBadge({ variant = "ghost" }) {
  const cls = variant === "solid"
    ? "bg-gray-900 text-white border-gray-900"
    : "bg-gray-100 text-gray-600 border-gray-200";
  return (
    <span className={`ml-1 inline-flex items-center text-[10px] font-semibold rounded-full px-2 py-0.5 border ${cls}`}>
      Admin only
    </span>
  );
}

/** Collapsible section with monospace pre-formatted body. */
function DebugSection({ label, value, defaultOpen = false, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  const isEmpty = !value || !String(value).trim();
  return (
    <div className="bg-white border border-blue-100 rounded-md overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-blue-50/50 transition-colors"
      >
        <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">{label}</span>
        <span className="text-[10px] text-blue-500">{open ? "Hide" : (isEmpty ? "Empty" : "Show")}</span>
      </button>
      {open && (
        <pre
          className="text-[11px] text-gray-700 whitespace-pre-wrap font-mono px-3 pb-3 pt-0 max-h-80 overflow-y-auto"
          data-testid={`${testid}-body`}
        >
          {isEmpty ? "(empty)" : String(value)}
        </pre>
      )}
    </div>
  );
}

/** Collapsible section that renders a list of attachments with download
 *  links. Each row shows filename, type, size; clicking the filename or
 *  the "Download" pill triggers the existing `/api/files/{id}/download`
 *  endpoint (which is GridFS-backed and works cross-pod). */
function AttachmentList({ label, items, testid }) {
  const [open, setOpen] = useState(true);
  const count = (items || []).length;
  const fmtSize = (b) => {
    if (!b) return "—";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };
  return (
    <div className="bg-white border border-blue-100 rounded-md overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-blue-50/50 transition-colors"
      >
        <span className="text-xs font-bold text-blue-800 uppercase tracking-wide">
          {label} · {count}
        </span>
        <span className="text-[10px] text-blue-500">{open ? "Hide" : (count === 0 ? "Empty" : "Show")}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 pt-0" data-testid={`${testid}-body`}>
          {count === 0 ? (
            <p className="text-[11px] text-gray-400 italic py-1">No attachments.</p>
          ) : (
            <ul className="space-y-1.5">
              {items.map((it) => (
                <li
                  key={it.id}
                  className="flex items-center justify-between gap-2 text-[11px] py-1.5 px-2 bg-gray-50 rounded"
                  data-testid={`${testid}-item-${it.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-800 truncate">
                      {it.filename || it.id}
                    </div>
                    <div className="text-gray-500">
                      {it.content_type || "—"} · {fmtSize(it.size_bytes)}
                      {it.file_type && <span className="ml-1.5 italic">({it.file_type})</span>}
                      {it.has_gridfs && (
                        <span
                          className="ml-1.5 inline-flex items-center text-[9px] font-medium rounded px-1 py-0 border bg-green-50 text-green-700 border-green-200"
                          title="Backed by GridFS — survives cross-pod cold storage"
                        >
                          GridFS
                        </span>
                      )}
                    </div>
                  </div>
                  <a
                    href={`${API}${it.download_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[10px] font-semibold px-2 py-1 rounded bg-gray-900 text-white hover:bg-black transition-colors"
                    data-testid={`${testid}-download-${it.id}`}
                  >
                    Download
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── WokeloResearchPanel ─────────────────────────────────────────────
// Renders the audit trail for the research stage when the user picked
// `approach="research"`. Surfaces:
//   • Which backend produced the prose (Wokelo Pro / Deep research /
//     HasData fallback) + the user's tier choice.
//   • The exact query string the worker sent into Wokelo.
//   • Each interrupt Wokelo posed (verify_companies / clarifying_question)
//     AND the Opus arbiter's verbatim response we sent back.
//   • A clickable chat_id deep-link for cross-system debugging.
//   • Source list (URL + title) so admins can reconcile what was cited
//     vs. what made it into the slide content.
function WokeloResearchPanel({ wokelo }) {
  if (!wokelo || !wokelo.provider) return null;
  const PROVIDER_LABELS = {
    wokelo_pro: "Wokelo Pro (Public)",
    wokelo_deep_research: "Wokelo Deep Research (Premium)",
    hasdata_fallback: "HasData (Wokelo failover)",
    hasdata: "HasData (legacy)",
  };
  const providerLabel = PROVIDER_LABELS[wokelo.provider] || wokelo.provider;
  const interrupts = Array.isArray(wokelo.interrupts) ? wokelo.interrupts : [];
  const sources = Array.isArray(wokelo.sources) ? wokelo.sources : [];
  return (
    <div data-testid="dbg-wokelo-panel">
      <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2 flex items-center gap-2">
        Stage 0 — Research (Wokelo Chat API)
        <span className="text-[10px] font-semibold normal-case tracking-normal px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-800 border border-blue-200" data-testid="dbg-wokelo-provider-badge">
          {providerLabel}
        </span>
        {wokelo.research_model && (
          <span className="text-[10px] font-medium normal-case tracking-normal text-blue-700">
            tier: <span className="font-semibold">{wokelo.research_model}</span>
          </span>
        )}
      </h4>
      <div className="space-y-2">
        {wokelo.chat_id && (
          <div className="text-[11px] text-blue-700 flex items-center gap-2" data-testid="dbg-wokelo-chat-id">
            <span className="font-medium">Wokelo chat_id:</span>
            <code className="bg-blue-50 px-1.5 py-0.5 rounded text-[10px]">{wokelo.chat_id}</code>
            {wokelo.chat_url && (
              <a
                href={wokelo.chat_url}
                target="_blank"
                rel="noreferrer"
                className="text-blue-600 hover:underline font-medium"
                data-testid="dbg-wokelo-chat-link"
              >
                Open chat ↗
              </a>
            )}
          </div>
        )}
        <DebugSection
          label="A. Query sent to Wokelo (verbatim)"
          value={wokelo.query_sent}
          defaultOpen
          testid="dbg-wokelo-query"
        />
        {interrupts.length > 0 && (
          <div className="border border-amber-200 rounded-md bg-amber-50/40" data-testid="dbg-wokelo-interrupts-block">
            <div className="px-3 py-2 text-[11px] font-semibold text-amber-900 border-b border-amber-200">
              B. Wokelo interrupts handled by Opus 4.7 ({interrupts.length})
            </div>
            <div className="p-3 space-y-3 text-[11px]">
              {interrupts.map((it, idx) => (
                <div key={idx} className="bg-white rounded border border-amber-200 p-2.5">
                  <div className="text-amber-800 font-semibold mb-1">
                    [{idx + 1}] {it.kind === "verify_companies" ? "Company verification" : it.kind === "clarifying_question" ? "Clarifying question" : it.kind}
                  </div>
                  {it.kind === "verify_companies" && it.choice && (
                    <div className="space-y-0.5 text-gray-700">
                      <div>Picked: <code className="bg-gray-100 px-1 rounded">{it.choice.permalink || "?"}</code></div>
                      <div>Input name: <code className="bg-gray-100 px-1 rounded">{it.choice.input_name || "?"}</code></div>
                    </div>
                  )}
                  {it.kind === "clarifying_question" && (
                    <div className="space-y-1">
                      <div className="text-gray-700"><span className="font-medium">Wokelo asked:</span> {it.question}</div>
                      <div className="text-gray-700"><span className="font-medium">Opus replied:</span> <span className="text-emerald-700">{it.answer}</span></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        <DebugSection
          label={`C. Prose returned by Wokelo (${wokelo.prose_len || 0} chars)`}
          value={wokelo.prose}
          testid="dbg-wokelo-prose"
        />
        {sources.length > 0 && (
          <div className="border border-blue-100 rounded-md bg-blue-50/30" data-testid="dbg-wokelo-sources-block">
            <div className="px-3 py-2 text-[11px] font-semibold text-blue-900 border-b border-blue-200">
              D. Sources cited ({sources.length})
            </div>
            <ul className="p-3 space-y-1 text-[11px]">
              {sources.map((s, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-gray-500 shrink-0">[{i + 1}]</span>
                  <a href={s.url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline truncate">
                    {s.title || s.url}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}



function SlideDebugBody({ debug }) {
  // New (2026-05-15) spec per @user — matches the exact mental model
  // they wanted surfaced:
  //
  //   Stage 1 (Slide Content Generation):
  //     1. Slide content prompt (template)
  //     2. User prompt:
  //        2a. Title
  //        2b. Content
  //     3. Research output (HasData, if applicable)
  //     4. Output → Slide content (= what flows into Stage 2 as the
  //                                 final user prompt to Opus)
  //
  //   Stage 2 (PPT Generation):
  //     5. System prompt (for PPT generation)
  //     6. Final user prompt to Opus (should equal #4)
  //
  // Claude text blocks / assistant_response dropped — not useful to the
  // admin debug workflow per @user.
  const s1 = debug?.stage1_slide_content || {};
  const s2 = debug?.stage2_ppt_generation || {};
  const tpl = debug?.template || {};
  const wokelo = debug?.wokelo_research || {};
  return (
    <div className="space-y-4">
      <div className="text-[11px] text-blue-700">
        Template: <span className="font-semibold">{tpl.name || "Default"}</span>
        {debug?.approach && (
          <span className="ml-3">
            Approach: <span className="font-semibold uppercase">{debug.approach}</span>
          </span>
        )}
      </div>

      <WokeloResearchPanel wokelo={wokelo} />

      <div>
        <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
          Stage 1 — Slide Content Generation
        </h4>
        <div className="space-y-2">
          <DebugSection
            label="1. Slide content prompt"
            value={s1.slide_content_prompt}
            testid="dbg-stage1-prompt"
          />
          <DebugSection
            label="2a. User prompt — Title"
            value={s1.user_title}
            defaultOpen
            testid="dbg-stage1-title"
          />
          <DebugSection
            label="2b. User prompt — Content"
            value={s1.user_content}
            defaultOpen
            testid="dbg-stage1-content"
          />
          <DebugSection
            label={`3. Research output${wokelo?.provider ? ` (${wokelo.provider})` : " (provider unknown)"}`}
            value={s1.research_output}
            testid="dbg-stage1-research"
          />
          <AttachmentList
            label="Content attachments (sent as PDF/image blocks or extracted text)"
            items={s1.content_attachments}
            testid="dbg-stage1-attachments"
          />
          <DebugSection
            label="4. Output → Slide content (fed to Stage 2)"
            value={s1.output_slide_content}
            defaultOpen
            testid="dbg-stage1-output"
          />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
          Stage 2 — PPT Generation
        </h4>
        <div className="space-y-2">
          <DebugSection
            label="5. System prompt"
            value={s2.system_prompt}
            testid="dbg-stage2-system"
          />
          <DebugSection
            label="6. Final user prompt to Opus (≈ #4)"
            value={s2.final_user_prompt}
            defaultOpen
            testid="dbg-stage2-final"
          />
          <div className="bg-blue-100/60 border border-blue-200 rounded-md px-3 py-2 text-[10px] text-blue-900">
            <span className="font-bold">Image source rule:</span> if a style
            reference is uploaded it is the ONLY image sent. Otherwise the
            template's design references are used. Current source:{" "}
            <span className="font-semibold uppercase">
              {s2.image_source_used || "none"}
            </span>
          </div>
          <AttachmentList
            label="Style reference (inspiration_paths)"
            items={s2.inspiration_attachments}
            testid="dbg-stage2-inspiration"
          />
          <AttachmentList
            label="Template design refs (used when no style reference)"
            items={s2.template_design_refs}
            testid="dbg-stage2-design-refs"
          />
        </div>
      </div>
    </div>
  );
}

function DeckDebugBody({ debug }) {
  // Decks follow the same Stage-1 / Stage-2 model:
  //
  //   Stage 1 (Slide Content Generation) — runs ONCE for the whole deck:
  //     1. Multi-slide prompt (template's `multi_slide_prompt`)
  //     2a. Title (deck.topic)
  //     2b. Content (deck.content)
  //     3. Research output (HasData prose)
  //     4. Output → raw storyline markdown (gets split into per-slide
  //                                          chunks for Stage 2)
  //
  //   Stage 2 (PPT Generation) — runs ONCE PER CHILD SLIDE:
  //     5. System prompt
  //     6. Final user prompt to Opus (the per-slide content chunk
  //                                   produced by the storyline split)
  //
  // Claude text blocks intentionally NOT shown per @user spec.
  // ─── render ─────────────────────────────────────────────────────
  const s1 = debug?.stage1_slide_content || {};
  const slides = debug?.stage2_ppt_generation_per_slide || [];
  const refs = debug?.reference_images || {};
  const tpl = debug?.template || {};
  const wokelo = debug?.wokelo_research || {};
  return (
    <div className="space-y-4">
      <div className="text-[11px] text-blue-700">
        Template: <span className="font-semibold">{tpl.name || "Default"}</span>
        {debug?.approach && (
          <span className="ml-3">
            Approach: <span className="font-semibold uppercase">{debug.approach}</span>
          </span>
        )}
      </div>

      <WokeloResearchPanel wokelo={wokelo} />

      <div>
        <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
          Stage 1 — Storyline Generation
        </h4>
        <div className="space-y-2">
          <DebugSection
            label={
              s1.slide_content_prompt_source === "current_template_fallback"
                ? "1. Slide content prompt (multi-slide) ⚠️ current template — historical snapshot missing for legacy row"
                : "1. Slide content prompt (multi-slide) — historical snapshot from this run"
            }
            value={s1.slide_content_prompt}
            testid="dbg-stage1-prompt"
          />
          <DebugSection
            label="2a. User prompt — Title"
            value={s1.user_title}
            defaultOpen
            testid="dbg-stage1-title"
          />
          <DebugSection
            label="2b. User prompt — Content"
            value={s1.user_content}
            defaultOpen
            testid="dbg-stage1-content"
          />
          <DebugSection
            label={`3. Research output${wokelo?.provider ? ` (${wokelo.provider})` : " (provider unknown)"}`}
            value={s1.research_output}
            testid="dbg-stage1-research"
          />
          <AttachmentList
            label="Content attachments (sent as PDF/image blocks or extracted text)"
            items={s1.content_attachments}
            testid="dbg-stage1-attachments"
          />
          <DebugSection
            label="4. Output → Storyline (split per slide for Stage 2)"
            value={s1.output_slide_content}
            defaultOpen
            testid="dbg-stage1-output"
          />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
          Reference images (used at Stage 2)
        </h4>
        <div className="space-y-2">
          <div className="bg-blue-100/60 border border-blue-200 rounded-md px-3 py-2 text-[10px] text-blue-900">
            <span className="font-bold">Multi-slide image rule:</span>{" "}
            {refs.note || "Style references are ignored for multi-slide flows; only template design refs ship to Opus."}
            {" Current source: "}
            <span className="font-semibold uppercase">
              {refs.image_source_used || "none"}
            </span>
          </div>
          <AttachmentList
            label="Style reference uploaded by user (IGNORED for multi-slide)"
            items={refs.inspiration_attachments_ignored}
            testid="dbg-refs-inspiration-ignored"
          />
          <AttachmentList
            label="Template design refs (the only images sent to Opus)"
            items={refs.template_design_refs}
            testid="dbg-refs-design"
          />
        </div>
      </div>

      <div>
        <h4 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-2">
          Stage 2 — PPT Generation ({slides.length} slide{slides.length === 1 ? "" : "s"})
        </h4>
        <div className="space-y-3">
          {slides.map((s, i) => (
            <div
              key={s.id || i}
              className="bg-white border border-blue-200 rounded-md p-3"
              data-testid={`dbg-slide-${i}`}
            >
              <p className="text-xs font-bold text-blue-700 mb-2">
                Slide {i + 1} · {s.title}
                {s.status && s.status !== "complete" && (
                  <span className="ml-2 inline-flex items-center text-[9px] font-medium rounded-full px-1.5 py-0.5 border bg-amber-50 text-amber-700 border-amber-200">
                    {s.status}
                  </span>
                )}
              </p>
              <div className="space-y-2">
                <DebugSection
                  label="5. System prompt"
                  value={s.system_prompt}
                  testid={`dbg-slide-${i}-system`}
                />
                <DebugSection
                  label="6. Final user prompt to Opus"
                  value={s.final_user_prompt}
                  defaultOpen
                  testid={`dbg-slide-${i}-final`}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── CostBreakdownCard ──────────────────────────────────────────────────
// Detailed cost-driver inspector for high-bill decks. Renders the spend
// stratified by phase (Stage 1 transform, Storyline split, Stage 2 per
// slide), then a "what drove the cost" pane with attachment counts,
// resume-loop counts, and cache hit ratio.
function CostBreakdownCard({ data, slides, isDeck, totalCost, fmtCost }) {
  const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString());

  // Aggregate per-slide breakdown for decks (sum across all slides).
  let aggBreakdown = null;
  if (isDeck && slides && slides.length) {
    aggBreakdown = {
      loop_count: 0,
      image_blocks_count: 0,
      inspiration_images_count: 0,
      design_ref_images_count: 0,
      content_images_count: 0,
      content_files_count: 0,
      elapsed_seconds: 0,
      _per_slide: [],
    };
    slides.forEach((s, i) => {
      const b = s.cost_breakdown || {};
      aggBreakdown.loop_count += b.loop_count || 0;
      aggBreakdown.image_blocks_count += b.image_blocks_count || 0;
      aggBreakdown.inspiration_images_count = Math.max(
        aggBreakdown.inspiration_images_count, b.inspiration_images_count || 0,
      );
      aggBreakdown.design_ref_images_count = Math.max(
        aggBreakdown.design_ref_images_count, b.design_ref_images_count || 0,
      );
      aggBreakdown.content_images_count = Math.max(
        aggBreakdown.content_images_count, b.content_images_count || 0,
      );
      aggBreakdown.content_files_count = Math.max(
        aggBreakdown.content_files_count, b.content_files_count || 0,
      );
      aggBreakdown.elapsed_seconds += b.elapsed_seconds || 0;
      aggBreakdown._per_slide.push({
        idx: i + 1,
        cost_usd: s.cost?.cost_usd || 0,
        loops: b.loop_count || 0,
        elapsed: b.elapsed_seconds || 0,
        cache_ratio: b.cache_hit_ratio,
        slide_id: s.id,
        title: s.title || s.inputs?.title || `Slide ${i + 1}`,
      });
    });
  }

  // Single-slide breakdown comes straight off the doc.
  const singleBreakdown = !isDeck ? (data.cost_breakdown || {}) : null;

  // Per-phase cost split for the header strip.
  const stage1Cost = data.stage1_cost?.cost_usd || 0;
  const splitCost = data.split_cost?.cost_usd || 0;
  const stage2Cost = isDeck
    ? (slides || []).reduce((acc, s) => acc + (s.cost?.cost_usd || 0) + (s.stage1_cost?.cost_usd || 0), 0)
    : (data.cost?.cost_usd || 0);
  const totalUsd = totalCost?.cost_usd || 0;
  const pct = (v) => (totalUsd > 0 ? `${Math.round((v / totalUsd) * 100)}%` : "—");

  // Cache hit ratio for the whole run — a low ratio means we kept paying
  // 1.0x for input tokens that the agentic resume-loop should have cached.
  const cacheCreation = totalCost?.cache_creation_tokens || 0;
  const cacheRead = totalCost?.cache_read_tokens || 0;
  const cacheTotal = cacheCreation + cacheRead;
  const cacheHitRatio = cacheTotal ? (cacheRead / cacheTotal) : null;

  return (
    <Card className="border-amber-200 bg-amber-50/30 shadow-none mb-6" data-testid="cost-breakdown-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-amber-700" />
          Cost Breakdown
          {totalUsd > 5 && (
            <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-red-100 text-red-700 border border-red-200">
              High-Cost
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Phase split */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="cost-phase-split">
          <div>
            <p className="text-[11px] text-amber-700 mb-0.5">Stage 1 (Opus transform)</p>
            <p className="text-sm font-bold text-amber-900">${stage1Cost.toFixed(4)} <span className="text-[10px] font-normal text-amber-600">({pct(stage1Cost)})</span></p>
          </div>
          {isDeck && (
            <div>
              <p className="text-[11px] text-amber-700 mb-0.5">Storyline Split</p>
              <p className="text-sm font-bold text-amber-900">${splitCost.toFixed(4)} <span className="text-[10px] font-normal text-amber-600">({pct(splitCost)})</span></p>
            </div>
          )}
          <div>
            <p className="text-[11px] text-amber-700 mb-0.5">Stage 2 (slide gen)</p>
            <p className="text-sm font-bold text-amber-900">${stage2Cost.toFixed(4)} <span className="text-[10px] font-normal text-amber-600">({pct(stage2Cost)})</span></p>
          </div>
          <div>
            <p className="text-[11px] text-amber-700 mb-0.5">Cache Hit Ratio</p>
            <p className="text-sm font-bold text-amber-900">
              {cacheHitRatio == null ? "—" : `${Math.round(cacheHitRatio * 100)}%`}
              {cacheHitRatio != null && cacheHitRatio < 0.3 && (
                <span className="ml-1 text-[10px] font-bold text-red-700">LOW</span>
              )}
            </p>
          </div>
        </div>

        {/* Cache breakdown bar */}
        {cacheTotal > 0 && (
          <div className="mb-4">
            <p className="text-[11px] text-amber-700 mb-1">Cache Tokens</p>
            <div className="flex gap-1 text-[11px]">
              <div className="flex-1 bg-red-100 border border-red-200 rounded px-2 py-1">
                <span className="text-red-700 font-semibold">Created (1.0x)</span>:{" "}
                {fmtN(cacheCreation)}
              </div>
              <div className="flex-1 bg-emerald-100 border border-emerald-200 rounded px-2 py-1">
                <span className="text-emerald-700 font-semibold">Read (0.1x)</span>:{" "}
                {fmtN(cacheRead)}
              </div>
            </div>
            {cacheHitRatio != null && cacheHitRatio < 0.3 && (
              <p className="text-[11px] text-red-700 mt-1">
                ⚠ Cache miss rate is high — the agent likely re-sent large inputs across resume loops without hitting cache. Consider reducing MAX_RESUME_LOOPS or input payload.
              </p>
            )}
          </div>
        )}

        {/* Cost drivers — single-slide */}
        {!isDeck && singleBreakdown && Object.keys(singleBreakdown).length > 0 && (
          <div data-testid="cost-drivers-single">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Cost Drivers (this slide)</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs">
              <Metric
                label="Resume loops"
                value={`${singleBreakdown.loop_count ?? 0} / ${singleBreakdown.max_resume_loops ?? "?"}`}
                warn={(singleBreakdown.loop_count ?? 0) >= 10}
                hint="Each loop replays cache-creation tokens (1.0x)"
              />
              <Metric
                label="Template design images"
                value={singleBreakdown.design_ref_images_count ?? 0}
                warn={(singleBreakdown.design_ref_images_count ?? 0) >= 5}
                hint="Each image adds ~1.5K input tokens per call"
              />
              <Metric
                label="Inspiration images"
                value={singleBreakdown.inspiration_images_count ?? 0}
              />
              <Metric
                label="Content uploads"
                value={singleBreakdown.content_files_count ?? 0}
                warn={(singleBreakdown.content_files_count ?? 0) >= 5}
                hint="Each file is ingested in Stage 1 — large PDFs are the worst offender"
              />
              <Metric
                label="Image blocks total"
                value={singleBreakdown.image_blocks_count ?? 0}
              />
              <Metric
                label="Elapsed"
                value={singleBreakdown.elapsed_seconds ? `${Math.round(singleBreakdown.elapsed_seconds)}s` : "—"}
              />
            </div>
            {data.stage1_cost && (
              <div className="mt-3 pt-3 border-t border-amber-200 text-[11px] text-amber-800">
                <span className="font-semibold">Stage 1 inputs:</span>{" "}
                {data.stage1_cost.attachments_count ?? 0} attachments,{" "}
                {fmtN(data.stage1_cost.research_chars)} research chars,{" "}
                {fmtN(data.stage1_cost.raw_text_chars)} user-content chars,{" "}
                est input <strong>{fmtN(data.stage1_cost.estimated_input)}</strong> tokens
                {data.stage1_cost.empty_output && (
                  <span className="ml-2 text-red-700 font-semibold">[empty output — bill incurred but no useful prose returned]</span>
                )}
              </div>
            )}
          </div>
        )}

        {/* Cost drivers — deck (per-slide table) */}
        {isDeck && aggBreakdown && (
          <div data-testid="cost-drivers-deck">
            <p className="text-xs font-semibold text-amber-800 uppercase tracking-wide mb-2">Per-Slide Cost Drivers</p>
            <div className="overflow-x-auto border border-amber-200 rounded bg-white">
              <table className="w-full text-xs">
                <thead className="bg-amber-100 text-amber-900">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold">#</th>
                    <th className="px-2 py-1.5 text-left font-semibold">Title</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Cost</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Loops</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Cache %</th>
                    <th className="px-2 py-1.5 text-right font-semibold">Elapsed</th>
                  </tr>
                </thead>
                <tbody>
                  {aggBreakdown._per_slide.map((s) => (
                    <tr key={s.slide_id} className="border-t border-amber-100">
                      <td className="px-2 py-1.5">{s.idx}</td>
                      <td className="px-2 py-1.5 max-w-[260px] truncate" title={s.title}>{s.title}</td>
                      <td className={`px-2 py-1.5 text-right font-semibold ${s.cost_usd > 3 ? "text-red-700" : "text-amber-900"}`}>
                        ${s.cost_usd.toFixed(4)}
                      </td>
                      <td className={`px-2 py-1.5 text-right ${s.loops >= 10 ? "text-red-700 font-bold" : ""}`}>
                        {s.loops || 0}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {s.cache_ratio == null ? "—" : `${Math.round(s.cache_ratio * 100)}%`}
                      </td>
                      <td className="px-2 py-1.5 text-right">{s.elapsed ? `${Math.round(s.elapsed)}s` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 text-xs mt-3">
              <Metric
                label="Total resume loops"
                value={aggBreakdown.loop_count}
                warn={aggBreakdown.loop_count >= 30}
                hint="High counts → cache-creation token bleed across slides"
              />
              <Metric
                label="Template design imgs/slide"
                value={aggBreakdown.design_ref_images_count}
                warn={aggBreakdown.design_ref_images_count >= 5}
                hint="Each image adds tokens to every slide gen call"
              />
              <Metric
                label="Content uploads"
                value={aggBreakdown.content_files_count}
                warn={aggBreakdown.content_files_count >= 5}
                hint="File ingestion happens in Stage 1 (per slide for decks)"
              />
              <Metric
                label="Inspiration images"
                value={aggBreakdown.inspiration_images_count}
              />
              <Metric
                label="Image blocks total"
                value={aggBreakdown.image_blocks_count}
              />
              <Metric
                label="Deck elapsed (sum)"
                value={aggBreakdown.elapsed_seconds ? `${Math.round(aggBreakdown.elapsed_seconds)}s` : "—"}
              />
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value, warn, hint }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] text-amber-700 uppercase tracking-wide">{label}</span>
      <span className={`font-semibold ${warn ? "text-red-700" : "text-amber-900"}`} title={hint}>
        {value}
        {warn && <span className="ml-1 text-[10px] font-bold">⚠</span>}
      </span>
    </div>
  );
}

